import React, { FC, useCallback } from "react"
import { hasValue } from "../../../../../Models/Client/FetchState"
import { Banner } from "../../../../Common/Banner/Banner"
import { useAppStatus } from "../../../../../Hooks/useAppStatus"
import { AppStatusCanoticCrowd } from "../../../../../Models/Client/AppStatusCanoticCrowd"
import { routePaths } from "../../../../../Routes"
import { Link } from "resha"
import { LinkButton } from "../../../../Common/LinkButton/LinkButton"
import { useDispatch } from "react-redux"
import { ModalViewName } from "../../../../../Models/Client/Modal"
import { openModal } from "../../../../../Actions/Client/ModalActions"
import { useAppInstance } from "../../../../../Hooks/useAppInstance"

interface BatchStatusBannerProps {
    className?: string
    orgUsername: string
    appID: string
}

export const BatchStatusBanner: FC<BatchStatusBannerProps> = ({
    className,
    orgUsername,
    appID
}) => {
    const dispatch = useDispatch()
    const instanceFetch = useAppInstance(appID)
    const appStatusFetch = useAppStatus(appID)
    const handleAddPaymentMethod = useCallback(
        () => dispatch(openModal(ModalViewName.billing)),
        [dispatch]
    )

    if (
        !hasValue(instanceFetch) ||
        !hasValue(appStatusFetch) ||
        instanceFetch.value.batchProcessCount <= 0
    ) {
        return null
    }

    const count = instanceFetch.value.batchProcessCount
    const countText = `${count} data ${count === 1 ? "point" : "points"}`
    const lastCountText = count === 1 ? "data point" : `${count} data points`
    const pronoun = count === 1 ? "it" : "them"
    const beVerb = count === 1 ? "is" : "are"

    switch (appStatusFetch.value) {
        case AppStatusCanoticCrowd.calculatingMetrics:
            return (
                <Banner icon="info" className={className}>
                    The last {lastCountText} will be shown here once we've
                    calculated your{" "}
                    <Link
                        to={routePaths.appCustomizeMetrics(orgUsername, appID)}
                    >
                        performance options
                    </Link>{" "}
                    and you've reviewed {pronoun}
                </Banner>
            )
        case AppStatusCanoticCrowd.paymentMethodMissing:
            return (
                <Banner icon="warn" className={className}>
                    The last {lastCountText} will be shown here once you{" "}
                    <LinkButton onClick={handleAddPaymentMethod}>
                        add your billing info
                    </LinkButton>
                </Banner>
            )
        case AppStatusCanoticCrowd.acceptCalibrationJobsRequired:
            return (
                <Banner icon="warn" className={className}>
                    The last {lastCountText} will be shown here once you accept
                    preliminary pricing (see above)
                </Banner>
            )
        case AppStatusCanoticCrowd.configureMetricsRequired:
            if (instanceFetch.value.enterprise) {
                return (
                    <Banner icon="info" className={className}>
                        The last {lastCountText} will be shown here shortly.
                        Please contact your customer success agent for more info
                    </Banner>
                )
            }

            return (
                <Banner icon="warn" className={className}>
                    The last {lastCountText} will be shown here once you review
                    your{" "}
                    <Link
                        to={routePaths.appCustomizeMetrics(orgUsername, appID)}
                    >
                        performance options
                    </Link>
                </Banner>
            )
        default:
            return (
                <Banner icon="info" className={className}>
                    {countText} {beVerb} still being created and will be shown
                    here shortly
                </Banner>
            )
    }
}
